import { App } from '@capacitor/app';
import { platform } from './identity';

export const LIFECYCLE_ACTIVE = 'active';
export const LIFECYCLE_BACKGROUND = 'background';

export function createLifecycleController({ native = platform.isNative, app = App, doc = typeof document !== 'undefined' ? document : undefined, win = typeof window !== 'undefined' ? window : undefined } = {}) {
  let state = doc?.visibilityState === 'hidden' ? LIFECYCLE_BACKGROUND : LIFECYCLE_ACTIVE;
  const listeners = new Set();
  let cleanup = null;

  const emit = (next, source, force = false) => {
    if (!force && next === state) return;
    state = next;
    listeners.forEach((listener) => listener({ state, source }));
  };

  const start = () => {
    if (cleanup) return;
    if (native) {
      let handle;
      let cancelled = false;
      Promise.resolve(app.addListener('appStateChange', ({ isActive }) => {
        emit(isActive ? LIFECYCLE_ACTIVE : LIFECYCLE_BACKGROUND, 'native');
      })).then((value) => {
        if (cancelled) value?.remove?.();
        else handle = value;
      });
      cleanup = () => { cancelled = true; handle?.remove?.(); };
      return;
    }

    const onVisibility = () => emit(doc?.visibilityState === 'hidden' ? LIFECYCLE_BACKGROUND : LIFECYCLE_ACTIVE, 'visibility');
    const onFocus = () => {
      if (doc?.visibilityState !== 'hidden') emit(LIFECYCLE_ACTIVE, 'focus');
    };
    const onBlur = () => emit(LIFECYCLE_BACKGROUND, 'blur');
    const onPageShow = (event) => {
      if (doc?.visibilityState !== 'hidden') emit(LIFECYCLE_ACTIVE, 'pageshow', Boolean(event?.persisted));
    };
    doc?.addEventListener?.('visibilitychange', onVisibility);
    win?.addEventListener?.('focus', onFocus);
    win?.addEventListener?.('blur', onBlur);
    win?.addEventListener?.('pageshow', onPageShow);
    cleanup = () => {
      doc?.removeEventListener?.('visibilitychange', onVisibility);
      win?.removeEventListener?.('focus', onFocus);
      win?.removeEventListener?.('blur', onBlur);
      win?.removeEventListener?.('pageshow', onPageShow);
    };
  };

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      start();
      return () => {
        listeners.delete(listener);
        if (!listeners.size && cleanup) { cleanup(); cleanup = null; }
      };
    },
  };
}

export const lifecycle = createLifecycleController();
